import { Injectable } from '@angular/core';
import { BlahService } from '@headpower/blah-ng';
import { OAuthService } from 'angular-oauth2-oidc';
import L, { CRS } from 'leaflet';

import { AppConfig } from 'src/app/app.config';
import { ProductService } from 'src/app/portal/product/portal-product.service';
import { IwmLocaleService } from 'src/app/services/iwm-locale.service';

@Injectable({
  providedIn: 'root'
})
export class MapLayerService {


  private accessToken: string;

  private baseBgLayers = {
    OpenStreetMap: L.tileLayer.wms(
      `${this.config.geoBaseUri}geoserver/headpower/wms?`,
      <L.WMSOptions>{
        layers: 'headpower:osm',
        tiled: true,
        format: 'image/png',
        version: '1.3.0',
        attribution: '© Omniscale 2022 (https://maps.omniscale.com) – Map data: OpenStreetMap (License ODbL)',
        minZoom: 1,
        maxZoom: 19,
        'ngsw-bypass': '',
      }
    ),
  }

  private satNetBgLayers = {
    SatNetMonitor: L.tileLayer.wms(
      `${this.config.geoBaseUri}geoserver/headpower/wms?`,
      <L.WMSOptions>{
        layers: 'headpower:satnet_rgb',
        crs: CRS.EPSG3857,
        format: 'image/jpeg',
        transparent: true,
        tiled: true,
        version: '1.3.0',
        minZoom: 1,
        maxZoom: 19,
        'ngsw-bypass': '',
      }
    ),
  }

  private extraBgLayers = {
    'Water color': L.tileLayer(
      'https://tiles-eu.stadiamaps.com/tiles/stamen_watercolor/{z}/{x}/{y}.jpg',
      {
        attribution: '&copy; <a href="https://stadiamaps.com/" target="_blank">Stadia Maps</a> <a href="https://stamen.com/" target="_blank">&copy; Stamen Design</a> &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
        minZoom: 1,
        maxZoom: 12,
        maxNativeZoom: 11,
      }
    ),

  }

  private country: string;
  private satnet: boolean | null = null;

  constructor(
    private oAuthService: OAuthService,
    private config: AppConfig,
    private iwmLocaleService: IwmLocaleService,
    private productService: ProductService,
    private blahService: BlahService,
  ) {

    this.accessToken = this.oAuthService.getAccessToken();

    const locale = iwmLocaleService.getLocale();
    this.country = locale.country;

    this.productService.hasProductUsageRight(this.config.iwmSatNetProductId).subscribe((res) => this.satnet = res);
  }


  public getAllBackgroundLayers(): any {
    return {
      ...this.baseBgLayers,
      ...this.getFinnishBgLayers(),
      ...this.satNetBgLayers,
      ...this.extraBgLayers
    }
  }

  public async getVisibleBackgroundLayers() {

    while (this.satnet === null) {
      // wait for initialization to complete
      await new Promise(f => setTimeout(f, 1000));
    }

    let finnishLayers = this.country === 'FI' ? this.getFinnishBgLayers() : {};
    let satNetLayers = this.satnet ? this.satNetBgLayers : {};

    return {
      ...this.baseBgLayers,
      ...finnishLayers,
      ...satNetLayers
    }
  }

  public getDefaultBackgroundLayerName(): string {
    if (this.country === 'FI') {
      return this.blahService.blah('map.layers.mml_bg');
    }
    return 'OpenStreetMap';
  }

  public async getVisibleOverlayLayers() {

    while (this.satnet === null) {
      // wait for initialization to complete
      await new Promise(f => setTimeout(f, 1000));
    }

    let finnishLayers = this.country === 'FI' ? this.getFinnishOverlayLayers() : {};
    let satNetLayers = this.satnet ? this.getSatNetOverlayLayers() : {};

    return {
      ...finnishLayers,
      ...satNetLayers
    }
  }

  private getFinnishBgLayers(): object {
    let result = {};

    // Layer names are object property keys in Leaflet. Get translated names and use them as keys.

    result[this.blahService.blah('map.layers.mml_bg')] = L.tileLayer.wms(
      `${this.config.geoBaseUri}geoserver/headpower/wms?`,
      <L.WMSOptions>{
        layers: 'headpower:mml_taustakartta',
        tiled: true,
        format: 'image/png',
        version: '1.3.0',
        attribution: 'Maanmittauslaitoksen ajantasainen taustakartta-aineisto',
        minZoom: 1,
        maxZoom: 19,
        maxNativeZoom: 18,
        bounds: L.latLngBounds(L.latLng(58.6074565294299, 15.053785270822841), L.latLng(70.26415662234614, 33.993537468175056)),
        'ngsw-bypass': '',
      }
    );

    result[this.blahService.blah('map.layers.mml_ortho')] = L.tileLayer.wms(
      `${this.config.geoBaseUri}geoserver/headpower/wms?`,
      <L.WMSOptions>{
        layers: 'headpower:mml_ortokuva',
        tiled: true,
        format: 'image/jpeg',
        version: '1.3.0',
        attribution: 'Maanmittauslaitoksen ajantasainen ortokuva-aineisto',
        minZoom: 1,
        maxZoom: 19,
        maxNativeZoom: 18,
        bounds: L.latLngBounds(L.latLng(58.6074565294299, 15.053785270822841), L.latLng(70.26415662234614, 33.993537468175056)),
        'ngsw-bypass': '',
      }
    );

    result[this.blahService.blah('map.layers.mml_ortho_nir')] = L.tileLayer.wms(
      `${this.config.geoBaseUri}geoserver/headpower/wms?`,
      <L.WMSOptions>{
        layers: 'headpower:mml_ortokuva_vaaravari',
        tiled: true,
        format: 'image/jpeg',
        version: '1.3.0',
        attribution: 'Maanmittauslaitoksen ajantasainen ortokuva (vääräväri)-aineisto',
        minZoom: 1,
        maxZoom: 19,
        maxNativeZoom: 18,
        bounds: L.latLngBounds(L.latLng(58.6074565294299, 15.053785270822841), L.latLng(70.26415662234614, 33.993537468175056)),
        'ngsw-bypass': '',
      }
    );

    result[this.blahService.blah('map.layers.mml_topo')] = L.tileLayer.wms(
      `${this.config.geoBaseUri}geoserver/headpower/wms?`,
      <L.WMSOptions>{
        layers: 'headpower:mml_maastokartta',
        tiled: true,
        format: 'image/png',
        version: '1.3.0',
        attribution: 'Maanmittauslaitoksen ajantasainen maastokartta-aineisto',
        minZoom: 1,
        maxZoom: 19,
        maxNativeZoom: 18,
        bounds: L.latLngBounds(L.latLng(58.6074565294299, 15.053785270822841), L.latLng(70.26415662234614, 33.993537468175056)),
        'ngsw-bypass': '',
      }
    );

    return result;
  }

  private getFinnishOverlayLayers(): object {
    let result = {};

    // Layer names are object property keys in Leaflet. Get translated names and use them as keys.

    result[this.blahService.blah('map.layers.cadastal_identifiers')] = L.tileLayer.wms(
      `${this.config.geoBaseUri}geoserver/headpower/wms?`,
      <L.WMSOptions>{
        layers: 'headpower:mml_kiinteistotunnukset',
        tiled: true,
        format: 'image/png',
        version: '1.3.0',
        attribution: 'todo',
        minZoom: 13,
        maxZoom: 19,
        maxNativeZoom: 18,
        'ngsw-bypass': '',
      }
    );

    result[this.blahService.blah('map.layers.cadastal_boundaries')] = L.tileLayer.wms(
      `${this.config.geoBaseUri}geoserver/headpower/wms?`,
      <L.WMSOptions>{
        layers: 'headpower:mml_kiinteistojaotus',
        tiled: true,
        format: 'image/png',
        version: '1.3.0',
        attribution: 'todo',
        minZoom: 13,
        maxZoom: 19,
        maxNativeZoom: 18,
        'ngsw-bypass': '',
      }
    );

    return result;
  }

  private getSatNetOverlayLayers(): object {
    let result = {};

    // Layer names are object property keys in Leaflet. Get translated names and use them as keys.

    result[this.blahService.blah('satnet.layers.strip')] = L.TileLayer.wmsHeader(
      `${this.config.backendMapUri}CommonMapWebAPI/api/Wms/GetSatelliteMapLayer?`,
      <L.WMSOptions>{
        layers: 'satnet_wide',
        format: 'image/png',
        transparent: true,
        crs: CRS.EPSG3857,
        minZoom: 10,
        maxZoom: 19,
      },
      [
        { header: 'Authorization', value: 'Bearer ' + this.accessToken },
        { header: 'ngsw-bypass', value: '' }
      ],
      null,
      null);

    result[this.blahService.blah('satnet.layers.vege2024')] = L.TileLayer.wmsHeader(
      `${this.config.backendMapUri}CommonMapWebAPI/api/Wms/GetSatelliteMapLayer?`,
      <L.WMSOptions>{
        layers: 'satnet_masked_vege_2024',
        format: 'image/png',
        transparent: true,
        crs: CRS.EPSG3857,
        minZoom: 12,
        maxZoom: 19,
      },
      [
        { header: 'Authorization', value: 'Bearer ' + this.accessToken },
        { header: 'ngsw-bypass', value: '' }
      ],
      null,
      null);

    return result;
  }
}

